import type { ApiErrorBody, ErrorCode, TokenPairResponse } from './types'

// DI через модульные переменные — разрывает циклическую зависимость auth ↔ client.
// AuthProvider вызывает configureClient() синхронно во время рендера, до первого запроса.
let getAccessToken: () => string | null = () => null
let getRefreshToken: () => string | null = () => null
let onTokenRefreshed: (pair: TokenPairResponse) => void = () => {}
let onSessionExpired: (code?: string | null) => void = () => {}

export interface ConfigureClientOptions {
  getAccessToken: () => string | null
  // Обязан читать актуальное значение (localStorage), а не кешированное в замыкании:
  // соседняя вкладка могла уже ротировать токен.
  getRefreshToken: () => string | null
  // /v0/auth/refresh РОТИРУЕТ refresh: старый становится недействительным сразу, а его
  // повторная отправка трактуется сервером как кража и гасит всю сессию (401 TOKEN_REUSE).
  onTokenRefreshed: (pair: TokenPairResponse) => void
  onSessionExpired: (code?: string | null) => void
}

export function configureClient(opts: ConfigureClientOptions): void {
  getAccessToken = opts.getAccessToken
  getRefreshToken = opts.getRefreshToken
  onTokenRefreshed = opts.onTokenRefreshed
  onSessionExpired = opts.onSessionExpired
}

export function getApiBaseUrl(): string {
  // window.__CONFIG__ инжектится /config.js перед main.tsx (docker-entrypoint.sh — прод,
  // public/config.js — dev). В dev пусто → относительные пути → их проксирует Vite.
  const runtime = typeof window !== 'undefined' ? window.__CONFIG__?.apiUrl : undefined
  return runtime ?? import.meta.env.VITE_API_URL ?? ''
}

export class ApiError extends Error {
  readonly status: number
  readonly code: ErrorCode | null
  readonly detail: string

  constructor(status: number, code: ErrorCode | null, detail: string) {
    super(detail || `HTTP ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.detail = detail
  }
}

interface RequestOptions {
  method?: string
  body?: unknown
  query?: Record<string, string | number | boolean | undefined>
  skipAuth?: boolean
  isRetry?: boolean
}

async function safeJson(res: Response): Promise<ApiErrorBody | null> {
  try {
    return (await res.json()) as ApiErrorBody
  } catch {
    return null
  }
}

// Refresh: параллельные 401 в ЭТОЙ вкладке должны делить одну попытку обновления.
let inflightRefresh: Promise<string> | null = null

// Refresh одноразовый, поэтому две вкладки не должны обновляться одновременно: вторая
// отправила бы уже потраченный токен и получила TOKEN_REUSE, выбив сессию в обеих.
// Web Lock сериализует их между вкладками; токен перечитывается ВНУТРИ лока — к этому
// моменту первая вкладка уже записала новый.
async function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  if (typeof navigator === 'undefined' || !navigator.locks) return fn()
  return navigator.locks.request('box-config-refresh', fn)
}

async function performRefresh(): Promise<string> {
  return withRefreshLock(async () => {
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      throw new ApiError(401, 'REQUIRED_TOKEN', 'Нет refresh-токена')
    }
    // box-config: refresh-токен передаётся в заголовке Authorization: Bearer <refresh>.
    const res = await fetch(`${getApiBaseUrl()}/v0/auth/refresh`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${refreshToken}` },
    })
    if (!res.ok) {
      const body = await safeJson(res)
      throw new ApiError(res.status, body?.code ?? null, body?.detail ?? 'Refresh failed')
    }
    const pair = (await res.json()) as TokenPairResponse
    // Сохраняем пару ЗДЕСЬ, до резолва промиса: inflightRefresh обнуляется раньше, чем
    // отработают .then() ожидающих, и следующий 401 успел бы прочитать старый refresh.
    onTokenRefreshed(pair)
    return pair.accessToken
  })
}

async function refreshOnce(): Promise<string> {
  if (!inflightRefresh) {
    inflightRefresh = performRefresh().finally(() => {
      inflightRefresh = null
    })
  }
  return inflightRefresh
}

// Завершение сессии на сервере: отзывает всю цепочку refresh-токенов. Best-effort —
// локальный выход выполняется в любом случае, ждать сеть пользователь не должен.
export async function revokeSession(): Promise<void> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) return
  try {
    await fetch(`${getApiBaseUrl()}/v0/auth/logout`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${refreshToken}` },
    })
  } catch {
    // сеть недоступна — токен протухнет сам
  }
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${getApiBaseUrl()}${path}`
  if (!query) return url
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) continue
    params.set(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, skipAuth = false, isRetry = false } = options

  const headers: Record<string, string> = {}
  if (!skipAuth) {
    const token = getAccessToken()
    if (token) headers['Authorization'] = `Bearer ${token}`
  }
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  const res = await fetch(buildUrl(path, query), {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (res.status === 401 && !skipAuth && !isRetry) {
    try {
      // Сохранение новой пары делает сам performRefresh — здесь только ждём и повторяем.
      await refreshOnce()
      return await request<T>(path, { ...options, isRetry: true })
    } catch (err) {
      onSessionExpired(err instanceof ApiError ? err.code : null)
      if (err instanceof ApiError) throw err
      throw new ApiError(401, 'INVALID_TOKEN', 'Сессия истекла')
    }
  }

  if (res.status === 204) return undefined as T
  if (!res.ok) {
    const errorBody = await safeJson(res)
    throw new ApiError(
      res.status,
      errorBody?.code ?? null,
      errorBody?.detail ?? `HTTP ${res.status}`,
    )
  }
  return (await res.json()) as T
}

export const http = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  del: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
}
